import React, { useEffect, useState } from "react";
import LayoutBuyerSeller from "../components/layout/LayoutBuyerSeller";
import { useAuth } from "../context/authContext";
import lineShape from "../assets/image/card-shape.svg";
import {
  FiAlertTriangle,
  FiArrowRight,
  FiCheck,
  FiCreditCard,
  FiLock,
  FiSettings,
  FiTruck,
} from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import apiService from "../actions/apiService";
import AcceptInvitationModal from "../components/models/AcceptInvitationModal";
import DeclineInvitationModal from "../components/models/DeclineInvitationModal";
import HomeIcon from "../assets/icon/home.svg";
import carIcon from "../assets/icon/car.svg";
import bagIcon from "../assets/icon/bag.svg";
import watchIcon from "../assets/icon/watch.svg";

// Map API category keys to their icon assets
const CATEGORY_ICON_BY_KEY = {
  car: carIcon,
  bag: bagIcon,
  watch: watchIcon,
  real_estate: HomeIcon,
  other: null,
};

// Map backend category to frontend category
const mapCategory = (category) => {
  const categoryMap = {
    car: "car",
    bag: "bag",
    watch: "watch",
    real_estate: "real_estate",
    other: "other",
  };
  return categoryMap[category] || category || "other";
};

const formatRelativeTime = (dateString) => {
  if (!dateString) return "—";

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "—";

  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) return "just now";
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60)
    return diffInMinutes === 1
      ? "about 1 minute ago"
      : `about ${diffInMinutes} minutes ago`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24)
    return diffInHours === 1
      ? "about 1 hour ago"
      : `about ${diffInHours} hours ago`;
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7)
    return diffInDays === 1
      ? "about 1 day ago"
      : `about ${diffInDays} days ago`;
  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4)
    return diffInWeeks === 1
      ? "about 1 week ago"
      : `about ${diffInWeeks} weeks ago`;
  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12)
    return diffInMonths === 1
      ? "about 1 month ago"
      : `about ${diffInMonths} months ago`;
  const diffInYears = Math.floor(diffInDays / 365);
  return diffInYears === 1
    ? "about 1 year ago"
    : `about ${diffInYears} years ago`;
};

const getActivityMeta = (status) => {
  const normalized = (status || "").toLowerCase();
  if (["released", "paid", "accepted"].includes(normalized)) {
    return {
      Icon: FiCreditCard,
      badgeClass: "bg-esbgsuccess",
      badgeText: "success",
    };
  }
  if (
    ["buyer_confirmed", "seller_confirmed", "confirmed", "shipped"].includes(
      normalized
    )
  ) {
    return {
      Icon: FiTruck,
      badgeClass: "bg-esTagColor",
      badgeText: "in progress",
    };
  }
  return {
    Icon: FiLock,
    badgeClass: "bg-esTagColor",
    badgeText: normalized || "pending",
  };
};

// Utility function to truncate text
const truncateText = (text, maxLength = 25) => {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
};

const ProfileOverview = ({
  hasRecent = false,
  isLoadingRecent = false,
  hasBankDetails = null,
  checkingBank = false,
  totalPurchases = 0,
  sellerRating = 0,
  trustScore = 0,
  statsLoading = false,
}) => {
  const { user } = useAuth();
  return (
    <div
      className="relative overflow-hidden rounded-2xl p-5 text-white shadow-sm border border-white/20"
      style={{
        background: "linear-gradient(57.04deg, #5A9AF8 0%, #2A8BF7 100%)",
      }}
    >
      {/* decorative tech lines */}
      <div
        className="absolute inset-0 left-[-110px] pointer-events-none"
        aria-hidden
      >
        <img
          src={lineShape}
          alt="ProtectPay"
          className="w-full opacity-35 invert h-full"
        />
      </div>

      <div className="flex items-center justify-between mb-8 relative z-10">
        <h3 className="text-big-price md:text-big-price-d !font-medium">
          Profile's overview
        </h3>
        <span className="bg-[#DCFCE7] text-[#16653F] border border-[#BBF7D0] text-[14px] font-semibold px-[10px] py-1 rounded-[50px] inline-flex items-center gap-1">
          <FiCheck className="w-3.5 h-3.5" />
          Verified
        </span>
      </div>

      <div className="flex items-center gap-4 relative z-10">
        {user?.profile_image_url ? (
          <img
            src={user?.profile_image_url}
            alt="avatar"
            className="w-16 h-16 object-cover rounded-full"
          />
        ) : (
          <span className="text-slate-600 rounded-full object-cover bg-slate-200  w-16 h-16 text-xl font-semibold flex items-center justify-center">
            {user?.firstName?.charAt(0) + user?.lastName?.charAt(0)}
          </span>
        )}
        <div className="flex-1">
          <div className="text-small-price-d opacity-75 font-normal">
            {user?.firstName} {user?.lastName}
          </div>
          <div className="text-[12px] opacity-75">
            {(() => {
              const createdAt = user?.created_at;
              if (!createdAt) {
                return "Member since N/A";
              }
              const date = new Date(createdAt);
              if (Number.isNaN(date.getTime())) {
                return "Member since N/A";
              }
              return `Member since ${date.toLocaleString(undefined, {
                month: "long",
                year: "numeric",
              })}`;
            })()}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 pt-4 text-xs relative z-10">
        <div>
          <div className="text-white opacity-75 text-[12px]">
            Total purchases
          </div>
          <div className="text-small-price-d font-bold">
            {statsLoading ? "0" : Number(totalPurchases || 0).toLocaleString()}
          </div>
        </div>
        <div className="text-right">
          <div className="text-white opacity-75 text-[12px]">Trust score</div>
          <div className="text-small-price-d font-bold">
            {statsLoading ? "0%" : `${Number(trustScore || 0)}%`}
          </div>
        </div>
      </div>

      {/* Warning Banner - shown only after load and if there are no recent items */}
      {!checkingBank && hasBankDetails === false && (
        <div className="mt-4 relative z-10">
          <div className="bg-[#FFD9D9] rounded-[12px] p-2 flex items-center gap-1">
            <div className="flex-shrink-0">
              <FiAlertTriangle className="w-4 h-4 text-[#FF6E6E]" />
            </div>
            <div className="flex-1">
              <p className="text-[12px] text-[#FF6E6E]">
                Bank details missing! Go to{" "}
                <Link to={"/my-profile"} className="underline font-medium">
                  your profile
                </Link>
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 grid grid-cols-[1fr_auto] gap-3 items-center relative z-10">
        <Link
          to="/my-profile"
          className="w-full bg-[#F5F8FF] text-esblue text-nav-d font-semibold py-2.5 rounded-[8px] shadow-sm text-center hover:bg-blue-50 transition-colors"
        >
          See my profile
        </Link>
        <Link
          to="/account-details"
          className="p-2.5 rounded-[8px] text-white border border-[#FFFFFF40] transition-colors"
          aria-label="settings"
        >
          <FiSettings className="w-5 h-5 opacity-50" />
        </Link>
      </div>
    </div>
  );
};

const BuyerPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [checkingBank, setCheckingBank] = useState(false);
  const [hasBankDetails, setHasBankDetails] = useState(null);
  const [buyerStats, setBuyerStats] = useState({
    totalPurchases: 0,
    sellerRating: 0,
    trustScore: 0,
  });
  const [buyerStatsLoading, setBuyerStatsLoading] = useState(false);
  const [recentActivity, setRecentActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [latestListings, setLatestListings] = useState([]);
  const [latestListingsLoading, setLatestListingsLoading] = useState(false);
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [selectedInvite, setSelectedInvite] = useState(null);
  const [deliveryFee, setDeliveryFee] = useState(null);

  useEffect(() => {
    let active = true;
    const checkBank = async () => {
      if (!user?.id) return;
      setCheckingBank(true);
      try {
        const res = await apiService.get("/bank-accounts/check");
        if (!active) return;
        setHasBankDetails(!!res?.data?.exists);
      } catch (error) {
        if (active) {
          setHasBankDetails(null);
        }
      } finally {
        if (active) {
          setCheckingBank(false);
        }
      }
    };
    checkBank();
    return () => {
      active = false;
    };
  }, [user?.id]);

  useEffect(() => {
    let active = true;
    const fetchBuyerStats = async () => {
      if (!user?.id) return;
      setBuyerStatsLoading(true);
      try {
        const res = await apiService.get("/transactions/buyer-stats");
        if (!active) return;
        if (res?.success && res.data) {
          setBuyerStats({
            totalPurchases: res.data.totalPurchases || 0,
            sellerRating:
              typeof res.data.sellerRating === "number"
                ? res.data.sellerRating
                : 0,
            trustScore:
              typeof res.data.trustScore === "number" ? res.data.trustScore : 0,
          });
        } else {
          setBuyerStats({
            totalPurchases: 0,
            sellerRating: 0,
            trustScore: 95,
          });
        }
      } catch (_) {
        if (active) {
          setBuyerStats({
            totalPurchases: 0,
            sellerRating: 0,
            trustScore: 95,
          });
        }
      } finally {
        if (active) {
          setBuyerStatsLoading(false);
        }
      }
    };
    fetchBuyerStats();
    return () => {
      active = false;
    };
  }, [user?.id]);

  useEffect(() => {
    let active = true;
    const fetchRecentActivity = async () => {
      if (!user?.id) return;
      setActivityLoading(true);
      try {
        const res = await apiService.get("/transactions/buyer-activity", {
          limit: 3,
        });
        if (!active) return;
        setRecentActivity(res?.data?.activity || []);
      } catch (_) {
        if (active) setRecentActivity([]);
      } finally {
        if (active) setActivityLoading(false);
      }
    };
    fetchRecentActivity();
    return () => {
      active = false;
    };
  }, [user?.id]);

  useEffect(() => {
    let active = true;
    const fetchInvitations = async () => {
      if (!user?.id) return;
      setInvitationsLoading(true);
      try {
        const res = await apiService.get("/items/buyer/invitations", {
          page: 1,
          limit: 4,
        });
        if (!active) return;
        if (res.success) {
          const invites = res?.data?.invites || [];
          setInvitations(invites.slice(0, 4)); // Limit to 4 most recent
        }
      } catch (_) {
        if (active) setInvitations([]);
      } finally {
        if (active) setInvitationsLoading(false);
      }
    };
    fetchInvitations();
    return () => {
      active = false;
    };
  }, [user?.id]);

  useEffect(() => {
    let active = true;
    const fetchLatestListings = async () => {
      setLatestListingsLoading(true);
      try {
        const res = await apiService.get("/items/latest", {
          page: 1,
          limit: 4,
        });
        if (!active) return;
        if (res.success) {
          const listings = res?.data?.items || [];
          setLatestListings(listings);
        }
      } catch (_) {
        if (active) setLatestListings([]);
      } finally {
        if (active) setLatestListingsLoading(false);
      }
    };
    fetchLatestListings();
    return () => {
      active = false;
    };
  }, []);

  const handleAcceptRequest = async (invite) => {
    setSelectedInvite(invite || null);
    // Best-effort fetch of authentication fee for items that support it
    if (invite?.item?.delivered_to_door && !deliveryFee) {
      try {
        const feeRes = await apiService.get("/transactions/delivery-fee");
        if (feeRes.success && feeRes.data?.delivery_fee) {
          setDeliveryFee(Number(feeRes.data.delivery_fee));
        }
      } catch (_) {
        // ignore; modal will show "Not available"
      }
    }
    setAcceptModalOpen(true);
  };

  const handleDeclineRequest = (invite) => {
    setSelectedInvite(invite || null);
    setDeclineModalOpen(true);
  };

  const handleCloseAcceptModal = () => {
    setAcceptModalOpen(false);
    setSelectedInvite(null);
  };

  const handleCloseDeclineModal = () => {
    setDeclineModalOpen(false);
    setSelectedInvite(null);
  };

  const handleConfirmAccept = async (includeAuthentication) => {
    if (!selectedInvite?.id) {
      setAcceptModalOpen(false);
      return;
    }
    try {
      const wantsAuthentication = Boolean(
        includeAuthentication && selectedInvite?.item?.delivered_to_door
      );
      if (wantsAuthentication && !deliveryFee) {
        // eslint-disable-next-line no-console
        console.error(
          "Authentication fee information is not available. Please try again."
        );
        return;
      }
      const res = await apiService.patch(
        `/invites/${selectedInvite.id}/accept`,
        { is_delivered_door: wantsAuthentication }
      );
      if (res.success) {
        setInvitations((prev) =>
          (prev || []).map((inv) =>
            inv?.id === selectedInvite.id ? { ...inv, status: "accepted" } : inv
          )
        );
        navigate(`/buyer/payment-initiate/${selectedInvite?.item?.id}`);
        setAcceptModalOpen(false);
        setSelectedInvite(null);
      }
    } catch (error) {
      console.error("Failed to accept invitation:", error);
    }
  };

  const handleConfirmDecline = async () => {
    if (!selectedInvite?.id) {
      setDeclineModalOpen(false);
      return;
    }
    try {
      const res = await apiService.delete(
        `/invites/${selectedInvite.id}/cancel`
      );
      if (res.success) {
        setInvitations((prev) =>
          (prev || []).map((inv) =>
            inv?.id === selectedInvite.id
              ? { ...inv, status: "cancelled" }
              : inv
          )
        );
        setDeclineModalOpen(false);
        setSelectedInvite(null);
      }
    } catch (error) {
      console.error("Failed to cancel invitation:", error);
    }
  };

  return (
    <LayoutBuyerSeller>
      <div className="absolute lg:hidden top-0 left-0 h-96 w-full bg-blue-gradient"></div>
      <div className="relative z-[10]">
        <div className="mb-8 px-5 lg:px-0 lg:ps-5">
          <div className="flex items-center flex-wrap justify-between gap-8">
            <div>
              <h2 className="md:text-page-title-d text-page-title !font-[600] text-white lg:text-[#1E1E1E] font-rubik">
                Welcome back, {user?.firstName} {user?.lastName}{" "}
                <span role="img" aria-label="waving hand">
                  👋
                </span>
              </h2>
              <p className="text-small-price-d font-normal opacity-50 text-white lg:text-[#1E1E1E] mt-4">
                Here is what's happening with your escrow transactions this
                week.
              </p>
            </div>
            <Link
              to="/buyer/listings"
              className="flex max-w-[400px] w-full justify-center text-esblue lg:text-white font-[600] text-[14px] items-center bg-white lg:bg-blue-gradient rounded-[8px] p-[12px] gap-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                className="stroke-esblue lg:stroke-white"
              >
                <path
                  d="M19 19L13.0001 13M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8Z"
                  stroke=""
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div>Search an item</div>
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
          <div className="lg:col-span-3 px-5 lg:px-0 lg:ps-5 order-2 lg:order-1">
            {/* My latest invitations */}
            <div className="">
              <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
                <div className="flex items-center justify-between mb-8">
                  <h4 className="md:text-big-price-d text-big-price text-esdarkGray">
                    My latest invitations
                  </h4>
                </div>
                {invitationsLoading ? (
                  <div className="py-4 text-center text-sm text-gray-500">
                    Loading invitations...
                  </div>
                ) : invitations.length === 0 ? (
                  <div className="py-4 text-center text-sm text-gray-500">
                    No invitations found
                  </div>
                ) : (
                  <>
                    <div className="w-full overflow-x-auto">
                      <table className="w-full min-w-[900px]">
                        <tbody>
                          {invitations.map((i, idx) => (
                            <tr key={idx} className="">
                              <td className="py-2 px-5">
                                <div className="flex items-center gap-4">
                                  <div className="h-[48px] w-[48px] bg-gray-100 rounded overflow-hidden">
                                    {i?.item?.images?.[0]?.file_url ? (
                                      <img
                                        src={`${process.env.REACT_APP_IMAGE_URL}${i?.item?.images?.[0]?.file_url}`}
                                        alt="thumb"
                                        className="h-full w-full object-cover"
                                      />
                                    ) : (
                                      <span className="h-[48px] w-[48px] flex items-center justify-center bg-gray-100 rounded text-gray-500">
                                        {"N/A"}
                                      </span>
                                    )}
                                  </div>
                                  <div>
                                    <div className="text-product-title text-esdarkGray line-clamp-1">
                                      {truncateText(i?.item?.title)}
                                    </div>
                                    <div className="text-[14px] opacity-75 text-[#000025]">
                                      {i?.item?.custom_category
                                        ? `${i?.item?.custom_category}`
                                        : i?.item?.category}
                                    </div>
                                  </div>
                                </div>
                              </td>

                              <td className="py-2 px-5 text-[#000025] text-page-subtitle-d">
                                {i?.item?.price} €
                              </td>

                              <td className="py-2 px-5">
                                <div className="flex items-center gap-4">
                                  <Link
                                    to={`/buyer/requests/${i?.id}/offer`}
                                    title="Message"
                                    className="h-10 w-fit px-3 flex items-center justify-center border border-gray-200 gap-4 rounded-[8px] bg-[#FFFFFF] text-[#1E1E1E] border:opacity-50"
                                  >
                                    <span className="text-[14px] font-[600]">
                                      View
                                    </span>
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="24"
                                      height="24"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                    >
                                      <g opacity="0.5">
                                        <path
                                          d="M2.42012 12.7132C2.28394 12.4975 2.21584 12.3897 2.17772 12.2234C2.14909 12.0985 2.14909 11.9015 2.17772 11.7766C2.21584 11.6103 2.28394 11.5025 2.42012 11.2868C3.54553 9.50484 6.8954 5 12.0004 5C17.1054 5 20.4553 9.50484 21.5807 11.2868C21.7169 11.5025 21.785 11.6103 21.8231 11.7766C21.8517 11.9015 21.8517 12.0985 21.8231 12.2234C21.785 12.3897 21.7169 12.4975 21.5807 12.7132C20.4553 14.4952 17.1054 19 12.0004 19C6.8954 19 3.54553 14.4952 2.42012 12.7132Z"
                                          stroke="#1E1E1E"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <path
                                          d="M12.0004 15C13.6573 15 15.0004 13.6569 15.0004 12C15.0004 10.3431 13.6573 9 12.0004 9C10.3435 9 9.0004 10.3431 9.0004 12C9.0004 13.6569 10.3435 15 12.0004 15Z"
                                          stroke="#1E1E1E"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </g>
                                    </svg>
                                  </Link>
                                  <div className="flex justify-center items-center gap-4">
                                    {i?.status === "pending" ? (
                                      <>
                                        <button
                                          title="Approve"
                                          onClick={() => handleAcceptRequest(i)}
                                          className="h-10 w-10 flex items-center justify-center rounded-[8px] transition-colors bg-[#B9FFCC] text-esdarkGray"
                                        >
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="18"
                                            height="13"
                                            viewBox="0 0 18 13"
                                            fill="none"
                                          >
                                            <path
                                              d="M17 1L6 12L1 7"
                                              stroke="#1E1E1E"
                                              strokeWidth="2"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            />
                                          </svg>
                                        </button>
                                        <button
                                          title="Reject"
                                          onClick={() =>
                                            handleDeclineRequest(i)
                                          }
                                          className="h-10 w-10 flex items-center justify-center rounded-[8px] transition-colors bg-[#FFD9D9] text-[#FF6E6E]"
                                        >
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="14"
                                            height="14"
                                            viewBox="0 0 14 14"
                                            fill="none"
                                          >
                                            <path
                                              d="M13 1L1 13M1 1L13 13"
                                              stroke="#FF6E6E"
                                              strokeWidth="2"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            />
                                          </svg>
                                        </button>
                                      </>
                                    ) : i?.status === "accepted" ? (
                                      <div className="flex gap-2 items-center">
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="18"
                                          height="13"
                                          viewBox="0 0 18 13"
                                          fill="none"
                                          className="min-w-[18px] min-h-[13px]"
                                        >
                                          <path
                                            d="M17 1L6 12L1 7"
                                            stroke="#16653F"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                        <span className="text-[#16653F] font-[600] text-[14px]">
                                          Invitation accepted
                                        </span>
                                      </div>
                                    ) : (
                                      <div className="flex gap-2 items-center">
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="14"
                                          height="14"
                                          viewBox="0 0 14 14"
                                          fill="none"
                                          className="min-w-[14px] min-h-[14px]"
                                        >
                                          <path
                                            d="M13 1L1 13M1 1L13 13"
                                            stroke="#FF0202"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                        <span className="text-[#FF0202] font-[600] text-[14px]">
                                          Declined
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="ms-auto w-fit mt-8">
                      {invitations.length > 0 && (
                        <Link
                          to="/buyer/requests"
                          className="text-[#2A8BF7] bg-[#F5F8FF] text-[14px] font-[600] flex items-center justify-center rounded-[8px] max-w-[170px] gap-1 p-[12px]"
                        >
                          See my invitations
                          <FiArrowRight className="w-3.5 h-3.5" />
                        </Link>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="lg:col-span-2 ms-0 bg-[#F5F8FF] lg:bg-transparent rounded-tl-[25px] lg:rounded-tl-[0px] rounded-tr-[25px] lg:rounded-tr-[0px] p-5 lg:p-0 lg:ms-0 order-1 lg:order-2">
            <ProfileOverview
              hasRecent={false}
              isLoadingRecent={false}
              hasBankDetails={hasBankDetails}
              checkingBank={checkingBank}
              totalPurchases={buyerStats.totalPurchases}
              sellerRating={buyerStats.sellerRating}
              trustScore={buyerStats.trustScore}
              statsLoading={buyerStatsLoading}
            />
          </div>
        </div>

        {/* Latest listings + Recent activity */}
        <div className="">
          {/* Recent activity */}
          <div className="mt-8 px-5 lg:px-0 lg:ps-5 items-start">
            <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
              <h4 className="md:text-big-price-d text-big-price text-esdarkGray mb-8">
                Recent activity
              </h4>
              <div>
                {activityLoading ? (
                  <div className="py-4 text-center text-sm text-gray-500">
                    Loading...
                  </div>
                ) : recentActivity.length === 0 ? (
                  <div className="py-4 text-center text-sm text-gray-500">
                    No recent activity
                  </div>
                ) : (
                  recentActivity.map((activity) => {
                    const { Icon, badgeClass, badgeText } = getActivityMeta(
                      activity.status
                    );
                    return (
                      <div
                        key={activity.id}
                        className="first:pt-0 py-4 last:pb-0 border-b border-[#1E1E1E0D] last:border-b-0"
                      >
                        <div className="px-0 lg:px-5">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <span className="mt-0.5 text-blue-600">
                                <Icon className="w-5 h-5" />
                              </span>
                              <div className="flex items-center gap-2">
                                <p className="text-tag-d font-semibold text-esdarkGray">
                                  {activity.title || "Transaction update"}
                                </p>
                              </div>
                            </div>
                            <span
                              className={`text-tag text-esGray md:text-tag-d px-[10px] py-1 rounded-[50px] ${badgeClass}`}
                            >
                              {badgeText}
                            </span>
                          </div>
                          <div className="mt-2">
                            <p className="text-body-d text-esDarkBlue opacity-50">
                              Transaction reference:{" "}
                              {activity.reference_code || activity.id}
                            </p>
                            <div className="flex items-center justify-between mt-2 gap-2">
                              <p className="text-[12px] text-esDarkBlue opacity-50">
                                {formatRelativeTime(activity.updated_at)}
                              </p>
                              <div className="text-esblue text-small-price-d font-bold">
                                {activity.amount != null
                                  ? `${Number(
                                      activity.amount
                                    ).toLocaleString()}€`
                                  : "—"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Latest listings */}
          <div className="mt-8 px-5 lg:px-0 lg:ps-5 items-start">
            <div className="bg-white border border-[#DCEDFF] rounded-xl p-5">
              <div className="flex flex-wrap gap-4 items-center justify-between mb-8">
                <h4 className="md:text-big-price-d text-big-price font-semibold text-gray-900">
                  Explore the latest listings
                </h4>
                <Link
                  to="/buyer/listings"
                  className="text-[#2A8BF7] bg-[#F5F8FF] text-[14px] font-[600] flex items-center justify-center rounded-[8px] max-w-[220px] gap-1 p-[12px]"
                >
                  Explore the marketplace
                </Link>
              </div>
              <div
                className={`grid overflow-y-auto gap-4 ${
                  latestListings.length === 0
                    ? "justify-center"
                    : "grid-cols-1 md:grid-cols-2 xl:grid-cols-4"
                }`}
              >
                {latestListingsLoading && (
                  <div className="text-sm my-4 text-gray-500">
                    Loading listings...
                  </div>
                )}
                {!latestListingsLoading && !latestListings.length && (
                  <div className="text-center py-8 text-sm text-gray-500">
                    No listings available
                  </div>
                )}
                {!latestListingsLoading &&
                  latestListings.map((item) => {
                    const categoryKey = mapCategory(
                      item.category || item.custom_category
                    );
                    const categoryIcon = CATEGORY_ICON_BY_KEY[categoryKey];
                    const imageBaseUrl = process.env.REACT_APP_IMAGE_URL || "";
                    const imageUrl = item.image
                      ? item.image.startsWith("http")
                        ? item.image
                        : `${imageBaseUrl}${item.image}`
                      : item.images?.[0]?.file_url
                      ? `${imageBaseUrl}${item.images[0].file_url}`
                      : null;

                    return (
                      <div
                        key={item.id}
                        className="bg-white w-full min-w-full border border-[#DCEDFF] rounded-[24px] overflow-hidden p-4"
                      >
                        <div className="relative h-44 bg-gray-100 rounded-[12px] overflow-hidden">
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={item.title}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-esdarkGray text-sm opacity-50">
                              No image
                            </div>
                          )}
                          {categoryIcon && categoryKey !== "other" && (
                            <button className="absolute right-3 top-3 text-white bg-[#1E1E1E40] rounded-[15px] h-[30px] w-[30px] flex items-center justify-center">
                              <img
                                src={categoryIcon}
                                alt={categoryKey}
                                className="w-4 h-4 invert"
                              />
                            </button>
                          )}
                        </div>

                        <div className="px-0 pt-5 pb-8">
                          <h3 className="font-medium leading-5 line-clamp-2 text-gray-900 break-words">
                            {item.title}
                          </h3>
                          <div className="mt-4">
                            <div className="text-esDarkBlue text-caption opacity-50">
                              Sale price
                            </div>
                            <div className="text-esblue font-semibold text-small-price-d">
                              {Number(item.price || 0).toFixed(2)} €
                            </div>
                          </div>
                        </div>

                        <div className="px-0 pb-0">
                          <Link
                            to={`/buyer/listings/${item.id}`}
                            className="w-full bg-[#F5F8FF] text-esblue text-caption-d py-3 rounded-[8px] flex items-center justify-center gap-2 text-center"
                          >
                            <span>View listing</span>
                          </Link>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Accept Invitation Modal */}
      <AcceptInvitationModal
        isOpen={acceptModalOpen}
        onClose={handleCloseAcceptModal}
        onAccept={handleConfirmAccept}
        buyerName={
          selectedInvite?.item?.seller?.firstName +
            " " +
            selectedInvite?.item?.seller?.lastName || ""
        }
        itemName={selectedInvite?.item?.title || ""}
        itemSupportsDelivery={Boolean(selectedInvite?.item?.delivered_to_door)}
        deliveryFee={deliveryFee}
      />

      {/* Decline Invitation Modal */}
      <DeclineInvitationModal
        isOpen={declineModalOpen}
        onClose={handleCloseDeclineModal}
        onDecline={handleConfirmDecline}
        buyerName={
          selectedInvite?.item?.seller?.firstName +
            " " +
            selectedInvite?.item?.seller?.lastName || ""
        }
        itemName={selectedInvite?.item?.title || ""}
      />
    </LayoutBuyerSeller>
  );
};

export default BuyerPage;
